import { useMemo, useState } from "react";
import { istBetrieb } from "./konsole-nav";
import { Link } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "../../components/Toast";
import { api } from "../../data";
import { Dialog, PillView, useAction, useRueckfrage } from "../../components/ui";
import { t } from "../../lib/sprache";
import { WEBSITE_URL } from "../../app/nav";
import {
  DAUERN, dayLabel, draftAfter, draftNextDay, draftProblems, draftsToPayload, groupByDay, languageLabel, naechsteFreieZeit, shortWhen, slotStatus, splitSlots, testkontoStand, timeRange,
  type DemoBooking, type DemoSlot, type SlotDraft,
} from "./demo-termine-model";
import { Fehler, Laden } from "./parts";
import { DemoAnfragen } from "./DemoAnfragen";
import { useKonsolenRolle } from "./rolle";

/**
 * Demo-Termine (0390, Inhaber 20.10.2026): Zeitfenster freigeben, die Interessenten auf creatoroffice.de/demo buchen; Liste der
 * kommenden und vergangenen Zeitfenster mit Buchung, Testkonto und Link; entfernen bzw. absagen mit Rückfrage. Alle Zeiten in
 * mitteleuropäischer Zeit (Europe/Berlin).
 */
const parse = <T,>(r: unknown): T => (typeof r === "string" ? JSON.parse(r) : r) as T;
export const DEMO_KEY = ["platform_demo_slots", "alle", "betrieb"] as const;
export const loadDemoSlots = async (reader: Pick<typeof api, "read">): Promise<DemoSlot[]> => parse<DemoSlot[]>(await reader.read("platform_demo_slots", {})) ?? [];

/** Plakette „Demo gebucht“ (Agenturliste, Agentur-Detail): Termin in deutscher Zeit, Link auf die Demo-Termine */
export function DemoGebucht({ mark }: { mark: { starts_at: string; name: string } | undefined }) {
  if (!mark) return null;
  const vorbei = Date.parse(mark.starts_at) < Date.now();
  return (
    <Link to="/intern/demo-termine" className="tk-demo-plakette" title={t(`Demo mit ${mark.name}`, `Demo with ${mark.name}`)}>
      <PillView pill={{ label: `${vorbei ? t("Demo war", "Demo held") : t("Demo gebucht", "Demo booked")} · ${shortWhen(mark.starts_at)}`, tone: vorbei ? "neutral" : "success", symbol: vorbei ? "○" : "✓" }} />
    </Link>
  );
}

export function DemoTermine() {
  const q = useQuery({ queryKey: DEMO_KEY, queryFn: () => loadDemoSlots(api) });
  const [nowMs] = useState(() => Date.now());
  const teile = useMemo(() => splitSlots(q.data ?? [], nowMs), [q.data, nowMs]);
  const gebucht = teile.kommend.filter((s) => s.booking);
  const frei = teile.kommend.filter((s) => !s.booking);
  const naechster = gebucht[0] ?? null;
  const ohneLink = gebucht.filter((s) => !s.meeting_url).length;
  // Vertrieb-Konsole (0459): der Vertrieb sieht Termine und Anfragen; Freigeben, Link, Absagen und Entfernen bleiben beim Admin
  const admin = istBetrieb(useKonsolenRolle());

  return (
    <>
      <title>{t("Demo-Termine · Plattform · CreatorOffice", "Demo bookings · Platform · CreatorOffice")}</title>
      {/* Inhaber 20.10.2026: übersichtlicher; oben das Wichtigste, gebuchte Termine als Karten, freie Zeiten kompakt, Freigeben daneben */}
      <div className="tk-demo-kopf">
        <div>
          <h1>{t("Demo-Termine", "Demo bookings")}</h1>
          <p className="muted">{t("Interessenten fragen auf creatoroffice.de/demo-anfrage eine Demo an; mit „Termine schicken“ bekommen sie deine freigegebenen Zeiten und buchen auf creatoroffice.de/demo. Alle Zeiten in mitteleuropäischer Zeit (Europe/Berlin).",
            "Prospects request a demo on creatoroffice.de/demo-anfrage; with “Send times” they get your open times and book on creatoroffice.de/demo. All times in Central European Time (Europe/Berlin).")}</p>
        </div>
        <a className="btn" href={`${WEBSITE_URL}/demo`} target="_blank" rel="noopener">{t("Seite ansehen", "View page")}</a>
      </div>
      {q.error ? <Fehler error={q.error} retry={() => void q.refetch()} /> : q.isPending ? <Laden /> : (
        <>
          <section className="grid four kpis tk-kacheln" aria-label={t("Überblick", "Overview")}>
            <div className="kpi">
              <small>{t("Nächster Termin", "Next call")}</small>
              <div className="metric num">{naechster ? shortWhen(naechster.starts_at) : "–"}</div>
              <small>{naechster?.booking ? `${naechster.booking.name} · ${naechster.booking.company}` : t("noch nichts gebucht", "nothing booked yet")}</small>
            </div>
            <div className="kpi">
              <small>{t("Gebucht", "Booked")}</small>
              <div className="metric num">{gebucht.length}</div>
              <small>{t("kommende Termine", "upcoming calls")}</small>
            </div>
            <div className="kpi">
              <small>{t("Frei", "Open")}</small>
              <div className="metric num">{frei.length}</div>
              <small>{frei.length ? t(`ab ${shortWhen(frei[0]!.starts_at)}`, `from ${shortWhen(frei[0]!.starts_at)}`) : t("keine freien Zeiten", "no open times")}</small>
            </div>
            <div className={`kpi${ohneLink ? " tk-demo-achtung" : ""}`}>
              <small>{t("Link fehlt", "Link missing")}</small>
              <div className="metric num">{ohneLink}</div>
              <small>{ohneLink ? t("bei gebuchten Terminen nachtragen", "add it for booked calls") : t("alle gebuchten haben einen Link", "all booked calls have a link")}</small>
            </div>
          </section>

          <div className="tk-demo-layout">
            <div className="stack tk-demo-haupt">
              <DemoAnfragen freieZeiten={frei.length} />
              <section className="stack" aria-labelledby="demo-gebucht-h">
                <h2 id="demo-gebucht-h" className="tk-abschnitt">{t("Gebuchte Termine", "Booked calls")} · {gebucht.length}</h2>
                {gebucht.length === 0
                  ? <div className="panel tk-empty"><p>{t("Noch keine Buchung. Sobald jemand bucht, steht der Termin hier und du bekommst eine Mail an hallo@.", "No bookings yet. As soon as someone books, the call appears here and you get an email at hallo@.")}</p></div>
                  : <Gebucht slots={gebucht} nowMs={nowMs} />}
              </section>
              <section className="stack" aria-labelledby="demo-frei-liste-h">
                <h2 id="demo-frei-liste-h" className="tk-abschnitt">{t("Freie Zeitfenster", "Open slots")} · {frei.length}</h2>
                {frei.length === 0
                  ? <div className="panel tk-empty"><p>{t("Keine freien Zeiten. Gib rechts welche frei.", "No open times. Open some on the right.")}</p></div>
                  : <Frei slots={frei} />}
              </section>
              <details className="tk-demo-vergangen">
                <summary>{t("Vergangen und entfernt", "Past and removed")} · {teile.vergangen.length}</summary>
                {teile.vergangen.length === 0
                  ? <p className="muted">{t("Noch nichts.", "Nothing yet.")}</p>
                  : <Vergangen slots={teile.vergangen} />}
              </details>
            </div>
            <aside className="stack tk-demo-seite">
              {admin && <Freigeben nowMs={nowMs} slots={teile.kommend} />}
              <section className="panel stack tk-demo-hinweise" aria-labelledby="demo-link-h">
                <h2 id="demo-link-h">{t("Persönlicher Link", "Personal link")}</h2>
                <p className="muted">{t("Wie /start: Name, Firma und Logo-Kürzel in der Adresse.", "Like /start: name, company and logo key in the address.")}</p>
                <code className="tk-demo-muster">creatoroffice.de/demo?n=Vorname&amp;f=Firma&amp;l=kuerzel</code>
                <p className="source-note">{t("Bestätigung mit Kalendereintrag an die buchende Person und hallo@creatoroffice.de, Erinnerung 24 Stunden vorher. Buchungen und Zeitfenster werden 90 Tage nach dem Termin gelöscht. Testkonto: Admin einer Agentur mit derselben E-Mail-Adresse.",
                  "Confirmation with calendar entry to the booker and hallo@creatoroffice.de, reminder 24 hours ahead. Bookings and slots are deleted 90 days after the call. Trial account: admin of an agency with the same email address.")}</p>
              </section>
            </aside>
          </div>
        </>
      )}
    </>
  );
}

function Freigeben({ nowMs, slots }: { nowMs: number; slots: DemoSlot[] }) {
  // K-556 b: Vorschlag ist die nächste freie Zeit, nicht immer morgen 10:00
  const [rows, setRows] = useState<SlotDraft[]>(() => [naechsteFreieZeit(slots, nowMs)]);
  const { busy, error, run } = useAction();
  const qc = useQueryClient();
  const probleme = draftProblems(rows, Date.now());
  const ok = probleme.every((p) => !p);
  const set = (i: number, patch: Partial<SlotDraft>) => setRows((r) => r.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const freigeben = async () => {
    const angelegt = rows;
    const r = await run("freigeben", async () => {
      await api.rpc("platform_demo_slots_create", { p_slots: draftsToPayload(angelegt) });
      return true;
    }, angelegt.length === 1 ? t("Zeitfenster freigegeben.", "Slot opened.") : t(`${angelegt.length} Zeitfenster freigegeben.`, `${angelegt.length} slots opened.`));
    if (r) setRows([naechsteFreieZeit(slots, Date.now(), angelegt[angelegt.length - 1]!.minutes, angelegt)]);
    // K-556 c: auch nach einem Fehler neu laden (die Verbindung kann abbrechen, obwohl die Zeitfenster angelegt sind)
    else void qc.invalidateQueries({ queryKey: DEMO_KEY });
  };

  return (
    <section className="panel stack" aria-labelledby="demo-frei-h">
      <h2 id="demo-frei-h">{t("Zeitfenster freigeben", "Open time slots")}</h2>
      <p className="muted">{t("Ohne Link steht in der Buchung „Link folgt“. Du kannst ihn später nachtragen, dann geht er per Mail an die buchende Person.",
        "Without a link the booking says “link to follow”. You can add it later; it is then emailed to the booker.")}</p>
      <ol className="tk-demo-zeilen">
        {rows.map((r, i) => (
          <li key={i} className="tk-demo-zeile">
            <label>{t("Datum", "Date")}<input type="date" value={r.day} onChange={(e) => set(i, { day: e.target.value })} aria-invalid={!!probleme[i] || undefined} /></label>
            <label>{t("Uhrzeit", "Time")}<input type="time" step={900} value={r.time} onChange={(e) => set(i, { time: e.target.value })} aria-invalid={!!probleme[i] || undefined} /></label>
            <label>{t("Dauer", "Duration")}
              <select value={r.minutes} onChange={(e) => set(i, { minutes: Number(e.target.value) })}>
                {DAUERN.map((d) => <option key={d} value={d}>{t(`${d} Minuten`, `${d} minutes`)}</option>)}
              </select>
            </label>
            <label className="tk-demo-link">{t("Meeting-Link", "Meeting link")}<input type="url" inputMode="url" value={r.link} placeholder={t("Link folgt", "Link to follow")} onChange={(e) => set(i, { link: e.target.value })} /></label>
            {rows.length > 1 && <button type="button" className="tk-demo-weg" onClick={() => setRows((x) => x.filter((_, j) => j !== i))} aria-label={t(`Zeile ${i + 1} entfernen`, `Remove row ${i + 1}`)}>{t("Zeile entfernen", "Remove row")}</button>}
            {probleme[i] && <p className="tk-error" role="alert">{probleme[i]}</p>}
          </li>
        ))}
      </ol>
      <div className="row tk-demo-plus">
        <button type="button" onClick={() => setRows((r) => [...r, draftAfter(r[r.length - 1]!)])} disabled={rows.length >= 60}>{t("+ Direkt im Anschluss", "+ Right after")}</button>
        <button type="button" onClick={() => setRows((r) => [...r, draftNextDay(r[r.length - 1]!)])} disabled={rows.length >= 60}>{t("+ Nächster Werktag", "+ Next workday")}</button>
      </div>
      {error && <p className="tk-error" role="alert">{error}</p>}
      <div>
        <button type="button" className="primary" disabled={!!busy || !ok} aria-busy={busy === "freigeben"} onClick={() => void freigeben()}>
          {busy === "freigeben" ? t("Wird freigegeben …", "Opening …") : rows.length === 1 ? t("Zeitfenster freigeben", "Open slot") : t(`${rows.length} Zeitfenster freigeben`, `Open ${rows.length} slots`)}
        </button>
      </div>
    </section>
  );
}

/** Link eines Zeitfensters: Adresse oder „Link fehlt“ (gebucht) bzw. „Link folgt“ (frei) */
function LinkAnzeige({ s }: { s: DemoSlot }) {
  if (s.meeting_url) return <a className="tk-demo-url" href={s.meeting_url} target="_blank" rel="noopener">{s.meeting_url.replace(/^https:\/\//, "")}</a>;
  return s.booking ? <PillView pill={{ label: t("Link fehlt", "Link missing"), tone: "warning", symbol: "!" }} /> : <span className="muted">{t("Link folgt", "Link to follow")}</span>;
}

function Gebucht({ slots, nowMs }: { slots: DemoSlot[]; nowMs: number }) {
  const admin = istBetrieb(useKonsolenRolle());
  const [absagen, setAbsagen] = useState<DemoSlot | null>(null);
  const [linkFuer, setLinkFuer] = useState<DemoSlot | null>(null);
  return (
    <>
      <ul className="tk-demo-karten">
        {slots.map((s) => {
          const b = s.booking!;
          return (
            <li key={s.id} className="panel tk-demo-karte">
              <div className="tk-demo-zeit">
                <strong className="num">{timeRange(s.starts_at, s.duration_minutes)}</strong>
                <span>{dayLabel(s.starts_at)}</span>
                <PillView pill={slotStatus(s)} />
              </div>
              <Bucher b={b} />
              <dl className="tk-demo-fakten">
                <div><dt>{t("Testkonto", "Trial account")}</dt><dd>{b.test_org ? <><Link to={`/intern/agenturen/${b.test_org.id}`}>{b.test_org.name}</Link> <small>· {testkontoStand(b.test_org, nowMs)}</small></> : <span className="muted">{t("noch keins", "none yet")}</span>}</dd></div>
                <div><dt>{t("Meeting-Link", "Meeting link")}</dt><dd><LinkAnzeige s={s} /></dd></div>
              </dl>
              {admin && <div className="row tk-demo-aktionen">
                <button type="button" className={s.meeting_url ? undefined : "primary"} onClick={() => setLinkFuer(s)}>{s.meeting_url ? t("Link ändern", "Change link") : t("Link eintragen", "Add link")}</button>
                <button type="button" className="danger" onClick={() => setAbsagen(s)}>{t("Absagen", "Cancel")}</button>
              </div>}
            </li>
          );
        })}
      </ul>
      <AbsageDialog slot={absagen} onClose={() => setAbsagen(null)} />
      <LinkDialog slot={linkFuer} onClose={() => setLinkFuer(null)} />
    </>
  );
}

function Frei({ slots }: { slots: DemoSlot[] }) {
  const admin = istBetrieb(useKonsolenRolle());
  const { busy, run } = useAction();
  const rueckfrage = useRueckfrage();
  const [linkFuer, setLinkFuer] = useState<DemoSlot | null>(null);
  const entfernen = async (s: DemoSlot) => {
    if (await rueckfrage.ask({ title: t("Zeitfenster entfernen?", "Remove slot?"), text: t(`${dayLabel(s.starts_at)}, ${timeRange(s.starts_at, s.duration_minutes)} Uhr ist danach nicht mehr buchbar.`, `${dayLabel(s.starts_at)}, ${timeRange(s.starts_at, s.duration_minutes)} can no longer be booked afterwards.`), confirmLabel: t("Entfernen", "Remove"), danger: true })) {
      void run(`weg-${s.id}`, () => api.rpc("platform_demo_slot_remove", { p_slot: s.id }), t("Zeitfenster entfernt.", "Slot removed."));
    }
  };
  return (
    <>
      {rueckfrage.element}
      <div className="panel tk-demo-freiliste">
        {groupByDay(slots).map((g) => (
          <section key={g.day} className="tk-demo-tag" aria-label={g.label}>
            <h3>{g.label}</h3>
            <ul>
              {g.slots.map((s) => (
                <li key={s.id}>
                  <span className="tk-demo-frei-zeit"><strong className="num">{timeRange(s.starts_at, s.duration_minutes)}</strong><small>{t(`${s.duration_minutes} Min.`, `${s.duration_minutes} min`)}</small></span>
                  <span className="tk-demo-frei-link"><LinkAnzeige s={s} />{s.cancelled.length > 0 && <small>{t(`${s.cancelled.length}× abgesagt, zuletzt ${s.cancelled[0]!.name}`, `${s.cancelled.length}× cancelled, last ${s.cancelled[0]!.name}`)}</small>}</span>
                  {admin && <span className="row tk-demo-frei-knoepfe">
                    <button type="button" onClick={() => setLinkFuer(s)}>{s.meeting_url ? t("Link ändern", "Change link") : t("Link eintragen", "Add link")}</button>
                    <button type="button" disabled={!!busy} aria-busy={busy === `weg-${s.id}`} onClick={() => void entfernen(s)}>{t("Entfernen", "Remove")}</button>
                  </span>}
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
      <LinkDialog slot={linkFuer} onClose={() => setLinkFuer(null)} />
    </>
  );
}

function Vergangen({ slots }: { slots: DemoSlot[] }) {
  return (
    <ul className="tk-demo-vergangen-liste">
      {slots.map((s) => (
        <li key={s.id}>
          <span className="num">{shortWhen(s.starts_at)}</span>
          <PillView pill={slotStatus(s)} />
          <span>{s.booking ? `${s.booking.name} · ${s.booking.company}` : s.cancelled[0] ? t(`abgesagt: ${s.cancelled[0].name}`, `cancelled: ${s.cancelled[0].name}`) : <span className="muted">{t("nicht gebucht", "not booked")}</span>}</span>
        </li>
      ))}
    </ul>
  );
}

function Bucher({ b }: { b: DemoBooking }) {
  return (
    <div className="tk-demo-bucher">
      <strong>{b.name}</strong>
      <span>{b.company}</span>
      <small><a href={`mailto:${b.email}`}>{b.email}</a> · {languageLabel(b.language)}</small>
      {b.previous_slot && <small>{t("Verschoben von ", "Moved from ")}{shortWhen(b.previous_slot.starts_at)}</small>}
      {b.note && <small className="tk-demo-notiz">„{b.note}“</small>}
      {b.reminder_sent_at && <small>{t("Erinnerung gesendet", "Reminder sent")}</small>}
    </div>
  );
}

function AbsageDialog({ slot, onClose }: { slot: DemoSlot | null; onClose: () => void }) {
  const [nachricht, setNachricht] = useState("");
  const [entfernen, setEntfernen] = useState(true);
  const { busy, error, run } = useAction();
  const b = slot?.booking ?? null;
  const absagen = async () => {
    if (!b) return;
    const r = await run("absagen", () => api.rpc("platform_demo_booking_cancel", { p_booking: b.id, p_message: nachricht.trim() || null, p_remove_slot: entfernen }),
      t("Termin abgesagt. Die Mail an die buchende Person ist im Postausgang.", "Booking cancelled. The email to the booker is queued."));
    if (r !== undefined) { setNachricht(""); onClose(); }
  };
  return (
    <Dialog open={!!slot} onClose={onClose} title={t("Termin absagen?", "Cancel booking?")}>
      {slot && b && (
        <>
          <p>{t(`${b.name} (${b.company}) bekommt eine Absage für ${dayLabel(slot.starts_at)}, ${timeRange(slot.starts_at, slot.duration_minutes)} Uhr, mit Kalender-Absage und dem Weg zu einem neuen Termin.`,
            `${b.name} (${b.company}) receives a cancellation for ${dayLabel(slot.starts_at)}, ${timeRange(slot.starts_at, slot.duration_minutes)}, with a calendar cancellation and a link to book a new time.`)}</p>
          <label className="stack" style={{ gap: 4 }}>{t("Nachricht in der Mail (optional)", "Message in the email (optional)")}
            <textarea rows={3} maxLength={1000} value={nachricht} onChange={(e) => setNachricht(e.target.value)} placeholder={t("z. B. Mir ist leider etwas dazwischengekommen.", "e.g. Something came up, sorry.")} />
          </label>
          <label className="row" style={{ gap: 8, alignItems: "flex-start" }}>
            <input type="checkbox" checked={entfernen} onChange={(e) => setEntfernen(e.target.checked)} />
            <span>{t("Zeitfenster danach nicht mehr anbieten", "Stop offering this slot afterwards")}</span>
          </label>
          {error && <p className="tk-error" role="alert">{error}</p>}
          <div className="row">
            <button type="button" className="danger" disabled={!!busy} aria-busy={busy === "absagen"} onClick={() => void absagen()}>{busy === "absagen" ? t("Wird abgesagt …", "Cancelling …") : t("Absagen und Mail senden", "Cancel and send email")}</button>
            <button type="button" onClick={onClose}>{t("Abbrechen", "Cancel")}</button>
          </div>
        </>
      )}
    </Dialog>
  );
}

function LinkDialog({ slot, onClose }: { slot: DemoSlot | null; onClose: () => void }) {
  const [url, setUrl] = useState("");
  const [fuer, setFuer] = useState<string | null>(null);
  const { busy, error, run } = useAction();
  if (slot && fuer !== slot.id) { setFuer(slot.id); setUrl(slot.meeting_url ?? ""); }
  const gueltig = !url.trim() || /^https:\/\/[^\s"<>\\]+$/.test(url.trim());
  const speichern = async () => {
    if (!slot) return;
    const r = await run("link", async () => parse<{ mailed: boolean }>(await api.rpc("platform_demo_slot_set_link", { p_slot: slot.id, p_url: url.trim() || null })));
    if (r !== undefined) {
      // K-556 f: sagen, dass die Mail rausgeht und an wen
      toast(r?.mailed && slot.booking ? t(`Link gespeichert und per Mail an ${slot.booking.name} geschickt.`, `Link saved and emailed to ${slot.booking.name}.`) : t("Link gespeichert.", "Link saved."));
      onClose();
    }
  };
  return (
    <Dialog open={!!slot} onClose={onClose} title={t("Meeting-Link", "Meeting link")}>
      {slot && (
        <>
          <p>{dayLabel(slot.starts_at)}, {timeRange(slot.starts_at, slot.duration_minutes)}</p>
          <label className="stack" style={{ gap: 4 }}>{t("Link (Google Meet, Zoom, Teams …)", "Link (Google Meet, Zoom, Teams …)")}
            <input type="url" inputMode="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://" aria-invalid={!gueltig || undefined} />
          </label>
          {slot.booking && <p className="muted">{t(`${slot.booking.name} bekommt den Link per Mail, mit aktualisiertem Kalendereintrag.`, `${slot.booking.name} receives the link by email with an updated calendar entry.`)}</p>}
          {!gueltig && <p className="tk-error" role="alert">{t("Der Link muss mit https:// beginnen.", "The link must start with https://.")}</p>}
          {error && <p className="tk-error" role="alert">{error}</p>}
          <div className="row">
            <button type="button" className="primary" disabled={!!busy || !gueltig} aria-busy={busy === "link"} onClick={() => void speichern()}>{t("Speichern", "Save")}</button>
            <button type="button" onClick={onClose}>{t("Abbrechen", "Cancel")}</button>
          </div>
        </>
      )}
    </Dialog>
  );
}
